import { Children, Fragment } from 'react';
import { getOpeningHours } from '@wojtekmaj/opening-hours-utils';
import T from '@wojtekmaj/react-t';
import { dequal } from 'dequal/lite';
import isValidEIN from 'is-valid-ein';
import isValidNIP from 'is-valid-nip';
import isValidUTR from 'is-valid-utr';
import { ExternalLinkIcon } from 'lucide-react';

import { locale } from '../i18n/i18n';
import { formatLongMonthDay, formatTime } from './date';
import { isAbsoluteOpeningHours, isRecurringOpeningHours } from './opening_hours';

import {
  COUNTRIES,
  COUNTRY_CALLING_CODES,
  COUNTRY_CODES,
  COUNTRY_NAMES,
  GOPOS_URL,
  INTEGRATION_NAMES,
  INTEGRATION_TYPES,
  INTEGRATIONS,
  MOBILE_PHONE_PREFIXES,
  PHONE_FORMATS,
  TIN_FORMATS,
  WEEKDAY_ABBRS,
  WEEKDAY_NAMES,
} from '../constants';

import type { PointsTransaction } from '@rewardopl/types';
import type { Address } from '@rewardopl/types/helpers/address';
import type { HourGroups, OpeningHours } from '../types';

type IrregularEvent = {
  label: string;
  monthDays: string[];
  year: number;
};

type EventOpeningHours = {
  hours: HourGroups;
  label: string;
};

type IrregularEventEntry = {
  dateKey: string;
  hours: HourGroups;
  index: number;
};

const IRREGULAR_EVENTS: IrregularEvent[] = [
  {
    // Polish shopping Sundays 2026
    label: 'Shopping Sundays',
    monthDays: ['Jan 25', 'Mar 29', 'Apr 26', 'Jun 28', 'Aug 30', 'Dec 06', 'Dec 13', 'Dec 20'],
    year: 2026,
  },
  {
    // Polish shopping Sundays 2027
    label: 'Shopping Sundays',
    monthDays: ['Jan 31', 'Mar 21', 'Apr 25', 'Jun 27', 'Aug 29', 'Dec 05', 'Dec 12', 'Dec 19'],
    year: 2027,
  },
  {
    // Polish shopping Sundays 2028
    label: 'Shopping Sundays',
    monthDays: ['Jan 30', 'Apr 09', 'Apr 30', 'Jun 25', 'Aug 27', 'Dec 03', 'Dec 10', 'Dec 17'],
    year: 2028,
  },
  {
    // Polish shopping Sundays 2029
    label: 'Shopping Sundays',
    monthDays: ['Jan 28', 'Mar 25', 'Apr 29', 'Jun 24', 'Aug 26', 'Dec 09', 'Dec 16', 'Dec 23'],
    year: 2029,
  },
];

function joinWithBr(arr: React.ReactNode[]): React.ReactNode {
  return Children.toArray(arr).reduce<React.ReactNode[]>((previousValue, value, index) => {
    if (value) {
      if (previousValue.length) {
        // biome-ignore lint/suspicious/noArrayIndexKey: index is stable here
        previousValue.push(<br key={index} />);
      }

      previousValue.push(value);
    }

    return previousValue;
  }, []);
}

export function formatAddress(addressObject?: Address | null): React.ReactNode {
  if (!addressObject) {
    return null;
  }

  const { address, postcode, city, state, country = 'PL' } = addressObject;

  const countryCode = (Object.keys(COUNTRY_CODES) as (keyof typeof COUNTRY_CODES)[]).find(
    (key) => COUNTRY_CODES[key] === country,
  );

  if (!countryCode) {
    throw new Error('Invalid country code');
  }

  const countryName = <T key="countryName">{COUNTRY_NAMES[countryCode]}</T>;

  switch (country) {
    case 'UK':
      return joinWithBr([address, city, postcode, countryName]);
    case 'US':
      return joinWithBr([
        address,
        <Fragment key="cityStateAndPostcode">
          {city}, {state} {postcode}
        </Fragment>,
        countryName,
      ]);
    case 'DE':
    case 'PL':
    default:
      return joinWithBr([
        address,
        <Fragment key="postcodeAndCity">
          {postcode} {city}
        </Fragment>,
        countryName,
      ]);
  }
}

export function formatBoolean(value?: boolean | null): string {
  if (value === undefined) {
    return 'n/a';
  }

  return value ? 'Yes' : 'No';
}

function formatCouponAvailabilityItem(
  {
    from: hoursFrom,
    to: hoursTo,
  }: {
    from: string;
    to?: string | null;
  },
  index: number,
): React.ReactNode {
  if (hoursFrom === '00:00' && hoursTo === '24:00') {
    return <T key={index}>All day</T>;
  }

  if (!hoursTo) {
    return `${formatTime(hoursFrom)} -`;
  }

  return `${formatTime(hoursFrom)} - ${formatTime(hoursTo)}`;
}

function join<T>(arr: T[], joiner = ','): (T | string)[] {
  return arr.reduce<(T | string)[]>((previousValue, value, index) => {
    if (index) {
      previousValue.push(joiner);
    }

    previousValue.push(value);

    return previousValue;
  }, []);
}

export function formatCouponAvailability(openingHoursString?: string | null): React.ReactNode {
  if (!openingHoursString) {
    return null;
  }

  const openingHoursArray = getOpeningHours(openingHoursString);

  if (!openingHoursArray || !openingHoursArray.length) {
    return null;
  }

  function findKey(value: string): keyof typeof WEEKDAY_ABBRS {
    const result = (Object.keys(WEEKDAY_ABBRS) as (keyof typeof WEEKDAY_ABBRS)[]).find(
      (key) => WEEKDAY_ABBRS[key] === value,
    );

    if (!result) {
      throw new Error('Invalid weekday abbreviation');
    }

    return result;
  }

  return (
    <ul style={{ listStyle: 'none' }}>
      {openingHoursArray.map(({ from, to, hours }, index) => {
        const fromKey = findKey(from);
        const toKey = findKey(to);

        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: index is stable here
          <li key={index}>
            <T>{WEEKDAY_NAMES[fromKey]}</T>
            {fromKey === toKey ? null : (
              <>
                {' - '}
                <T>{WEEKDAY_NAMES[toKey]}</T>
              </>
            )}
            : {join(hours.map(formatCouponAvailabilityItem), '; ')}
          </li>
        );
      })}
    </ul>
  );
}

function getDotykackaOrderLink({
  business_id: businessId,
  order_id: orderId,
}: Omit<NonNullable<PointsTransaction['integration_order']>, 'type'>): string | null {
  if (!businessId || !orderId) {
    return null;
  }

  return `https://admin.dotykacka.cz/cloud/${businessId}/receipt/${orderId}`;
}

function getGoPOSOrderLink({
  business_id: businessId,
  order_id: orderId,
}: Omit<NonNullable<PointsTransaction['integration_order']>, 'type'>): string | null {
  if (!businessId || !orderId) {
    return null;
  }

  return `${GOPOS_URL}/${businessId}/transactions/${orderId}`;
}

export function formatOrderId(
  {
    business_id: businessId,
    order_id: orderId,
    type,
  }: NonNullable<PointsTransaction['integration_order']>,
  props?: React.ComponentProps<'a'>,
): React.ReactElement {
  const getOrderLink = (() => {
    switch (type) {
      case INTEGRATION_TYPES.DOTYKACKA:
        return getDotykackaOrderLink;
      case INTEGRATION_TYPES.FOODSOFT:
        return null;
      case INTEGRATION_TYPES.GOPOS:
        return getGoPOSOrderLink;
      case INTEGRATION_TYPES.IZZYREST:
        return null;
      case INTEGRATION_TYPES.SOFTMAR_MM:
        return null;
      default:
        throw new Error(`Unknown order type: ${type satisfies never}`);
    }
  })();

  const integrationType = (Object.keys(INTEGRATIONS) as (keyof typeof INTEGRATIONS)[]).find(
    (key) => INTEGRATION_TYPES[key] === type,
  );

  if (!integrationType) {
    throw new Error('Invalid integration type');
  }

  const integrationName = INTEGRATION_NAMES[integrationType];

  const label = (
    <>
      {integrationName} {orderId}
    </>
  );

  if (!getOrderLink) {
    return label;
  }

  const href = getOrderLink({
    business_id: businessId,
    order_id: orderId,
  });

  if (!href) {
    return label;
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {label}{' '}
      <span style={{ position: 'relative', top: 1 }}>
        <ExternalLinkIcon size={16} />
      </span>
    </a>
  );
}

export function formatEmail(
  email?: string | null,
  props?: React.ComponentProps<'a'>,
): React.ReactNode {
  if (!email) {
    return null;
  }

  return (
    <a {...props} href={`mailto:${email}`}>
      {email}
    </a>
  );
}

export function formatFacebook(
  facebook?: string | null,
  props?: React.ComponentProps<'a'>,
): React.ReactNode {
  if (!facebook) {
    return '';
  }

  const fullUrl = `https://facebook.com/${facebook}`;

  return (
    <a {...props} href={fullUrl} rel="noopener noreferrer" target="_blank">
      {facebook}{' '}
      <span style={{ position: 'relative', top: 1 }}>
        <ExternalLinkIcon size={16} />
      </span>
    </a>
  );
}

export function formatInstagram(
  instagram?: string | null,
  props?: React.ComponentProps<'a'>,
): React.ReactNode {
  if (!instagram) {
    return '';
  }

  const instagramWithoutAt = instagram.replace(/^@/, '');
  const fullUrl = `https://instagram.com/${instagramWithoutAt}`;

  return (
    <a {...props} href={fullUrl} rel="noopener noreferrer" target="_blank">
      {instagram}{' '}
      <span style={{ position: 'relative', top: 1 }}>
        <ExternalLinkIcon size={16} />
      </span>
    </a>
  );
}

export function formatTiktok(
  tiktok: string | null,
  props?: React.ComponentProps<'a'>,
): React.ReactNode {
  if (!tiktok) {
    return '';
  }

  const tiktokWithoutAt = tiktok.replace(/^@/, '');
  const fullUrl = `https://www.tiktok.com/@${tiktokWithoutAt}`;

  return (
    <a {...props} href={fullUrl} rel="noopener noreferrer" target="_blank">
      {tiktok}{' '}
      <span style={{ position: 'relative', top: 1 }}>
        <ExternalLinkIcon size={16} />
      </span>
    </a>
  );
}

export function formatNumber(string?: number | null, maximumFractionDigits = 0) {
  if (string === null || string === undefined) {
    return '';
  }

  return new Intl.NumberFormat(locale, { maximumFractionDigits }).format(string);
}

export function formatLocation(
  location?: [number, number] | null,
  props?: React.ComponentProps<'a'>,
): React.ReactNode {
  if (!location) {
    return null;
  }

  return (
    <a
      {...props}
      href={`https://google.com/maps/place/${location[1]},${location[0]}`}
      rel="noopener noreferrer"
      target="_blank"
    >
      <T>See map</T>
    </a>
  );
}

function formatOpeningHour(
  {
    from: hoursFrom,
    to: hoursTo,
  }: {
    from: string;
    to?: string | null;
  },
  index: number,
): React.ReactNode {
  if (hoursFrom === '00:00' && hoursTo === '24:00') {
    return <T key={index}>Open 24h</T>;
  }

  if (!hoursTo) {
    return `${formatTime(hoursFrom)} -`;
  }

  return `${formatTime(hoursFrom)} - ${formatTime(hoursTo)}`;
}

function collectEventEntries(
  openingHoursArray: OpeningHours[],
  indicesToSkip: number[],
  monthDays: string[],
): IrregularEventEntry[] {
  const entries: IrregularEventEntry[] = [];

  for (const [index, openingHours] of openingHoursArray.entries()) {
    if (!isAbsoluteOpeningHours(openingHours) || indicesToSkip.includes(index)) {
      continue;
    }

    const { from, to } = openingHours;

    if (from !== to || !monthDays.includes(from)) {
      continue;
    }

    entries.push({
      dateKey: from,
      hours: openingHours.hours,
      index,
    });
  }

  return entries;
}

function areAllEventDatesCovered(entries: IrregularEventEntry[], monthDays: string[]): boolean {
  const matched = entries.map((entry) => entry.dateKey).sort();
  const expected = monthDays.toSorted();

  return dequal(matched, expected);
}

function areEventHoursUniform(entries: IrregularEventEntry[]): boolean {
  const firstEntry = entries[0];

  if (!firstEntry) {
    return false;
  }

  return entries.every((entry) => dequal(entry.hours, firstEntry.hours));
}

function aggregateIrregularEvents(openingHoursArray: OpeningHours[]) {
  const aggregatedEvents: EventOpeningHours[] = [];
  const indicesToSkip: number[] = [];
  const currentYear = new Date().getFullYear();

  for (const eventConfig of IRREGULAR_EVENTS) {
    const { label, monthDays, year } = eventConfig;

    if (year !== currentYear) {
      continue;
    }

    const matchingEntries = collectEventEntries(openingHoursArray, indicesToSkip, monthDays);
    const [firstEntry] = matchingEntries;

    if (
      !areAllEventDatesCovered(matchingEntries, monthDays) ||
      !areEventHoursUniform(matchingEntries) ||
      !firstEntry
    ) {
      continue;
    }

    aggregatedEvents.push({
      label,
      hours: firstEntry.hours,
    });

    for (const { index } of matchingEntries) {
      indicesToSkip.push(index);
    }
  }

  return {
    aggregatedEvents,
    indicesToSkip,
  };
}

/**
 * Formats opening hours string into a list of opening hours.
 *
 * @param {string} openingHoursString Opening hours string in OpenStreetMap format
 * @returns {React.ReactNode} List of opening hours
 */
export function formatOpeningHours(openingHoursString?: string | null): React.ReactNode {
  if (!openingHoursString) {
    return null;
  }

  const openingHoursArray = getOpeningHours(openingHoursString);

  if (!openingHoursArray || !openingHoursArray.length) {
    return null;
  }

  function findKey(value: string): keyof typeof WEEKDAY_ABBRS {
    const result = (Object.keys(WEEKDAY_ABBRS) as (keyof typeof WEEKDAY_ABBRS)[]).find(
      (key) => WEEKDAY_ABBRS[key] === value,
    );

    if (!result) {
      throw new Error('Invalid weekday abbreviation');
    }

    return result;
  }

  const { aggregatedEvents, indicesToSkip } = aggregateIrregularEvents(openingHoursArray);

  return (
    <ul style={{ listStyle: 'none' }}>
      {openingHoursArray.map((openingHours, index) => {
        if (indicesToSkip.includes(index)) {
          return null;
        }

        switch (true) {
          case isRecurringOpeningHours(openingHours): {
            const { from, to, hours } = openingHours;

            const fromKey = findKey(from);
            const toKey = findKey(to);

            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: index is stable here
              <li key={index}>
                <T>{WEEKDAY_NAMES[fromKey]}</T>
                {fromKey === toKey ? null : (
                  <>
                    {' - '}
                    <T>{WEEKDAY_NAMES[toKey]}</T>
                  </>
                )}
                : {join(hours.map(formatOpeningHour), '; ')}
              </li>
            );
          }
          case isAbsoluteOpeningHours(openingHours): {
            const { from, to, hours } = openingHours;

            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: index is stable here
              <li key={index}>
                {formatLongMonthDay(from)}
                {from === to ? null : ` - ${formatLongMonthDay(to)}`}:{' '}
                {join(hours.map(formatOpeningHour), '; ')}
              </li>
            );
          }
          default:
            throw new Error('Invalid opening hours');
        }
      })}
      {aggregatedEvents.map(({ label, hours }, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: index is stable here
        <li key={`event-${index}`}>
          <T>{label}</T>: {join(hours.map(formatOpeningHour), '; ')}
        </li>
      ))}
    </ul>
  );
}

export function formatPercentage(string?: number | null, maximumFractionDigits = 0) {
  if (string === null || string === undefined) {
    return '';
  }

  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits,
  }).format(Number(string) / 100);
}

function applyNumberFormat(string: string, format?: string | null): string {
  if (!string || !format) {
    return string;
  }

  const stringNumbersOnly = string.replace(/\D/g, '');

  let i = 0;

  return format
    .split('#')
    .map((el) => {
      const char = stringNumbersOnly[i++];
      return char ? el + char : '';
    })
    .join('');
}

export function formatPhone(
  phone?: string | null,
  props?: React.ComponentProps<'a'>,
): React.ReactNode {
  if (!phone) {
    return null;
  }

  let phoneNumbersOnly = phone.replace(/[^\d+]/g, '');

  if (!phoneNumbersOnly.startsWith('+')) {
    switch (phoneNumbersOnly.length) {
      case 11:
        // Only German mobile numbers are this long
        phoneNumbersOnly = `${COUNTRY_CALLING_CODES[COUNTRIES.GERMANY]}${phoneNumbersOnly}`;
        break;
      case 10:
        // Either UK or US number, we can't make a distinction here
        return phoneNumbersOnly;
      case 9:
        // Only Polish numbers are this long
        phoneNumbersOnly = `${COUNTRY_CALLING_CODES[COUNTRIES.POLAND]}${phoneNumbersOnly}`;
        break;
      case 8:
        // Only German landline numbers are this long
        phoneNumbersOnly = `${COUNTRY_CALLING_CODES[COUNTRIES.GERMANY]}${phoneNumbersOnly}`;
        break;
      default:
        // Unable to format the phone number
        return phoneNumbersOnly;
    }
  }

  const formattedPhone = (() => {
    const countryName = (() => {
      const [result] =
        (
          Object.entries(COUNTRY_CALLING_CODES) as [keyof typeof COUNTRY_CALLING_CODES, string][]
        ).find(([, value]) => phoneNumbersOnly?.startsWith(value)) || [];

      return result;
    })();

    if (countryName) {
      const countryCallingCode = COUNTRY_CALLING_CODES[countryName];
      const phoneWithoutCode = phoneNumbersOnly.slice(countryCallingCode.length);

      const isMobile = MOBILE_PHONE_PREFIXES[countryName].some((value) =>
        phoneWithoutCode.startsWith(value),
      );

      return `${countryCallingCode} ${applyNumberFormat(
        phoneWithoutCode,
        PHONE_FORMATS[countryName][isMobile ? 'MOBILE' : 'LANDLINE'],
      )}`;
    }

    return phoneNumbersOnly;
  })();

  return (
    <a {...props} href={`tel:${phone}`}>
      {formattedPhone}
    </a>
  );
}

export function formatCurrency(currency = 'PLN'): string {
  return (
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    })
      .formatToParts(1)
      .find((part) => part.type === 'currency')?.value || currency
  );
}

export function formatPrice(price?: number | null, currency = 'PLN'): string {
  if (price === null || price === undefined) {
    return '';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(price);
}

export function formatTIN(tin?: string | null): React.ReactNode {
  if (!tin) {
    return '';
  }

  switch (true) {
    case isValidEIN(tin):
      return applyNumberFormat(tin, TIN_FORMATS.UNITED_STATES);
    case isValidNIP(tin):
      return applyNumberFormat(tin, TIN_FORMATS.POLAND);
    case isValidUTR(tin):
      return applyNumberFormat(tin, TIN_FORMATS.UNITED_KINGDOM);
    default:
      return tin;
  }
}

export function formatWebsite(
  website?: string | null,
  props?: React.ComponentProps<'a'>,
): React.ReactNode {
  if (!website) {
    return '';
  }

  let host: string;
  let pathname: string;
  try {
    ({ host, pathname } = new URL(website));
  } catch {
    return website;
  }

  const { children = host + (pathname !== '/' ? pathname : ''), ...propsWithoutChildren } =
    props || {};

  return (
    <a {...propsWithoutChildren} href={website} rel="noopener noreferrer" target="_blank">
      {children}{' '}
      <span style={{ position: 'relative', top: 1 }}>
        <ExternalLinkIcon size={16} />
      </span>
    </a>
  );
}

export function highlightPattern(text: string | null, pattern: RegExp): React.ReactNode {
  if (!text) {
    return text;
  }

  const splitText = text.split(pattern);

  if (splitText.length <= 1) {
    return text;
  }

  const matches = text.match(pattern);

  if (!matches) {
    return text;
  }

  return splitText.reduce<React.ReactNode[]>((arr, element, index) => {
    const match = matches[index];

    arr.push(element);

    if (match) {
      arr.push(
        // biome-ignore lint/suspicious/noArrayIndexKey: index is stable here
        <mark key={index}>{matches[index]}</mark>,
      );
    }

    return arr;
  }, []);
}
